// src/pages/HRPayroll/SalaryPaymentList.js
import React, { useEffect, useState } from "react";
import moment from "moment";
import {
  Table,
  Button,
  Row,
  Col,
  Typography,
  Modal,
  App,
  Popconfirm,
  Space,
  Tooltip,
  Drawer,
  Grid
} from "antd";
import {
  ArrowLeftOutlined,
  PlusOutlined,
  EyeOutlined,
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchSalaryPayments, deleteSalaryPayment } from "./salaryPaymentThunk";
import {
  resetSalaryPayments,
  setSelectedSalaryPayment,
  setIsEditSalary,
  setRefreshList,
} from "./salaryPaymentSlice";
import { dateFormat, formatDateOnly, formatToCompanyTimezone, getAccessType } from "../../lib/functions";
import { approveSalaryPaymentApi } from "../../apiservice/hrpayroll";
import { useIsAdminUser } from "../../lib/accessList";
import { useTxLock } from "../../hooks/useTxLock";

const { Title } = Typography;

const SalaryPaymentList = () => {
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { modal } = App.useApp();
  const { list, loading, refreshList } = useSelector(
    (state) => state.salaryPayments
  );
const { isLocked: isTxLocked, lockDateFormatted } = useTxLock();
  // modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailItems, setDetailItems] = useState([]);
  let [access_type, access_type_set] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [helpOpen, setHelpOpen] = useState(false);
  const authInfo = useSelector((state) => state.auth?.auth_info);
  const isAdminUser = useIsAdminUser();

  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;


  useEffect(() => {
    const access = getAccessType("employee_entry");
    access_type_set(access);
  }, []);

  useEffect(() => {
    dispatch(resetSalaryPayments());
    dispatch(fetchSalaryPayments());
  }, [dispatch]);

  useEffect(() => {
    if (refreshList) {
      dispatch(fetchSalaryPayments());
      dispatch(setRefreshList(false));
    }
  }, [refreshList, dispatch]);

  const handleShowDetails = (record) => {
    setDetailItems(record.details || []);
    setDetailModalOpen(true);
  };

  const handleApprove = async (record) => {
    try {
      const res = await approveSalaryPaymentApi({ pay_id: record.pay_id });
      modal.success({ title: res.data.msg || "Payment approved successfully" });
      dispatch(fetchSalaryPayments());
    } catch (err) {
      modal.error({ title: err.response?.data?.message || "Approval failed" });
    }
  };

  const handleDelete = async (record) => {
    try {
      await dispatch(deleteSalaryPayment(record.pay_id)).unwrap();
      modal.success({ title: "Salary payment deleted successfully" });
    } catch (err) {
      modal.error({ title: err.message || "Failed to delete salary payment" });
    }
  };

  const columns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1,
      width: 60,
    },
    {
      title: "Payment Code",
      dataIndex: "pay_code",
    },
    {
      title: "Payment Date",
      dataIndex: "created_date",
      render: (val) => <span>{formatDateOnly(val)}</span> || "",
    },
    {
      title: "Pay From Account",
      dataIndex: "acc_name",
    },
    {
      title: "Month",
      dataIndex: "month",
    },
    {
      title: "Year",
      dataIndex: "year",
    },
    {
      title: "Total Amount",
      dataIndex: "total_amount",
      align: "right",
      render: (v) => Number(v || 0).toFixed(2),
    },
    {
      title: "Description",
      dataIndex: "narration",
    },
    {
      title: "Created By",
      dataIndex: "full_name",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (v) => (v === "p" ? "Pending" : "Done"),
    },
{
  title: "Actions",
  render: (_, record) => {
    const txLocked = isTxLocked(record?.created_date);

    return (
      <Space>
        {/* APPROVE */}
        {record.status === "p" && (
          <Tooltip
            title={
              txLocked
                ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
                : "Approve"
            }
          >
            <Popconfirm
              title="Approve this salary payment?"
              disabled={txLocked}
              onConfirm={() => {
                if (txLocked) return;

                handleApprove(record);
              }}
            >
              <Button
                icon={<CheckOutlined />}
                data-testid="approve-btn"
                type="link"
                disabled={txLocked}
                style={{ color: txLocked ? "#ccc" : "#1677ff" }}
              />
            </Popconfirm>
          </Tooltip>
        )}

        {/* EDIT */}
        {record.status === "p" && access_type === "WRITE" && (
          <Tooltip
            title={
              txLocked
                ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
                : "Edit"
            }
          >
            <Button
              type="link"
              icon={<EditOutlined />}
              disabled={!(isAdminUser || canEdit) || txLocked}
              onClick={() => {
                if (txLocked) return;

                dispatch(setSelectedSalaryPayment(record));
                dispatch(setIsEditSalary(true));

                navigate(
                  `/hrpayroll/salary-payments/edit/${record.pay_id}`
                );
              }}
            />
          </Tooltip>
        )}

        {/* DELETE */}
        {record.status === "p" && access_type === "WRITE" && (
          <Tooltip
            title={
              txLocked
                ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
                : "Delete"
            }
          >
            <Popconfirm
              title="Are you sure you want to delete this salary payment?"
              disabled={!(isAdminUser || canDelete) || txLocked}
              onConfirm={() => {
                if (!(isAdminUser || canDelete) || txLocked) return;

                handleDelete(record);
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                disabled={!(isAdminUser || canDelete) || txLocked}
              />
            </Popconfirm>
          </Tooltip>
        )}

        {/* VIEW */}
        <Tooltip title="View">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleShowDetails(record)}
          />
        </Tooltip>
      </Space>
    );
  },
}

  ];

  // modal table
  const detailColumns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1,
      width: 60,
    },
    {
      title: "Employee",
      dataIndex: "acc_name",
    },
    {
      title: "Pay Type",
      dataIndex: "pay_type",
      render: (val) =>
        val === "Monthly Payment" ? "Monthly Salary" : val,
    },
    {
      title: "Amount",
      dataIndex: "pay_amount",
      align: "right",
      render: (v) => Number(v || 0).toFixed(2),
    },
  ];

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />}
            onClick={() => navigate(-1)}
            data-testid="salary-payment-back-button"
          />
          <Title level={3} style={{ margin: 0 }}>
            Salary Payment List
          </Title>
        </div>

        <Tooltip title="Help">
          <Button
            type="text"
            icon={<QuestionCircleOutlined style={{ fontSize: 20 }} />}
            onClick={() => setHelpOpen(true)}
          />
        </Tooltip>
      </div>
      <Row justify="end" style={{ marginBottom: 32, marginTop: 32 }}>
        <Col>
          {access_type == "WRITE" && <>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                dispatch(setSelectedSalaryPayment(null));
                dispatch(setIsEditSalary(false));
                navigate("/hrpayroll/salary-payments/add");
              }}
            >
              Add New
            </Button>
          </>}

        </Col>
      </Row>

      <Table
        bordered
        loading={loading}
        columns={columns}
        dataSource={list}
        rowKey="pay_id"
        pagination={{
        current: currentPage,
        pageSize: pageSize,
        total: list.length,
        showQuickJumper: true,
        showSizeChanger: true,
        pageSizeOptions: [10, 20, 50, 100],
        onChange: (page) => {
          setCurrentPage(page);
        },
        onShowSizeChange: (_, size) => {
          setPageSize(size);   
          setCurrentPage(1);   
        },
        }}
        size="middle"
        scroll={{ x: "max-content" }}
        style={{
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          overflow: "hidden",
          backgroundColor: "#fff",
        }}
        rowClassName={(_, index) =>
          index % 2 === 0 ? "table-row-light" : "table-row-dark"
        }
      />

      <Modal
        title="Salary Payment Details"
        open={detailModalOpen}
        onCancel={() => {
          setDetailModalOpen(false);
          setDetailItems([]);
        }}
        footer={null}
        width={700}
      >
        <Table
          columns={detailColumns}
          dataSource={detailItems}
          rowKey={(r) => r.pay_d_id || `${r.to_acc_id}-${r.pay_type}`}
          pagination={false}
        />
      </Modal>
      <Drawer
        title="Salary Payment Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={430}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "12px"
            }}
          >
            Salary Payment Management
          </h2>

          <p style={{ color: "#555", marginBottom: "16px", marginLeft:14 }}>
            The Salary Payment module is used to manage employee salary payment
            transactions within the ERP HR & Payroll system. It helps process,
            approve, monitor, and track employee salary payments and payroll accounting workflows.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Available Actions</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>View salary payment records and payment history</li>
              <li>Create new employee salary payments</li>
              <li>Edit pending salary payment entries</li>
              <li>Delete pending salary payment transactions</li>
              <li>Approve employee salary payments</li>
              <li>Track payment month and year details</li>
              <li>Monitor salary payment totals and descriptions</li>
              <li>View employee-wise salary payment details</li>
              <li>Manage salary payment account mappings</li>
              <li>Review completed and pending payment statuses</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Approved salary payments cannot be edited or deleted.</li>
              <li>Salary payments affect payroll accounting records immediately.</li>
              <li>Only pending salary payments can be approved.</li>
              <li>Role-based access controls manage edit and delete permissions.</li>
              <li>Employee-wise salary details are available in detail view.</li>
              <li>Salary payment history is maintained for payroll auditing and reports.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default SalaryPaymentList;
